import React, { Dispatch, SetStateAction } from 'react'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
import { SelectedPlanItemsContext } from 'containers/SubPlan/layers/BL/SelectedItems'
import { MinimizedObjectivesListAPIContext } from 'containers/MinimizedObjectivesList/layers/API'
// hooks
import { useAddReferencedItem } from 'containers/MinimizedObjectivesList/layers/Business/useAddReferencedItem'
// types
import { TSourceItem } from 'containers/MinimizedObjectivesList/layers/Business/utils'
// types
import { IPlan, IPlanAction, IPlanKpi, IPlanObjective } from 'types/graphql'
// utils
import getOr from 'lodash/fp/getOr'
import maxBy from 'lodash/fp/maxBy'
import { handleError } from 'utils/errors/handlers'
import { flow, get, isFunction } from 'lodash'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  getUpdatedLastPosition: () => number
  handleAddToSubPlan: (node: TNode, plan: IPlan, sourceItems?: TSourceItem[]) => () => Promise<void>
  handleRemoveFromSubPlan: (node: TNode) => () => Promise<void>
  selectedPlanItems: any
  selectPlanItem: Dispatch<SetStateAction<any>>
  removePlanItem: (id: string) => void
}

type TNode = IPlanAction & IPlanKpi & IPlanObjective

export const MinimizedObjectivesListActionsContext = React.createContext<IContext>({} as any)

const MinimizedObjectivesListActions: React.FC<Props> = ({ children }) => {
  // state
  const [selectedPlanItems, selectPlanItem] = React.useState([])

  // context
  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)
  const { deletePlanItemMutation, planItems } = React.useContext(MinimizedObjectivesListAPIContext)
  const { removePlanItem: removePlanItemFromMinimizedObjectivesList } = React.useContext(SelectedPlanItemsContext)

  // hooks
  const addReferencedItem = useAddReferencedItem({ selectedPlanItems, selectPlanItem })

  const lastPosition = flow(maxBy('treePosition'), getOr(0, 'treePosition'))(planItems as any)

  const lastPositionRef = React.useRef(lastPosition)

  React.useEffect(() => {
    lastPositionRef.current = lastPosition
  }, [lastPosition])

  // methods
  const getUpdatedLastPosition = () => {
    lastPositionRef.current = lastPositionRef.current + 1

    return lastPositionRef.current
  }

  const handleRemoveSelectedPlanItem = (id) => {
    const filteredNodes = selectedPlanItems.filter(({ prevId }) => {
      return prevId !== id
    })

    selectPlanItem(filteredNodes)
  }

  // `sourceItems` are the items of the plan we pick from — they define the reference order
  const handleAddToSubPlan = (node, plan, sourceItems) => () =>
    addReferencedItem({
      entityType: node.entityType,
      entityId: node.entityId,
      treePosition: node.treePosition,
      planId: plan?.id,
      sourceItems,
      sourcePlanItemId: node.id
    })

  const handleRemoveFromSubPlan = (node) => async (nodeId?: string) => {
    const filteredNode = selectedPlanItems.filter((entity) => entity.prevId === node.id)

    try {
      await deletePlanItemMutation({
        variables: {
          id: get(filteredNode, '[0].newId') || nodeId,
          deleteSource: false,
          checkChildren: true
        }
      })

      if (isFunction(treeQueryRefetch)) {
        await treeQueryRefetch()
      }

      if (node) {
        handleRemoveSelectedPlanItem(node.id)
      }

      removePlanItemFromMinimizedObjectivesList(node.id)
    } catch (errors) {
      handleError(errors, 'MinimizedObjectivesListActions.handleRemoveFromSubPlan')
    }
  }

  const value = {
    getUpdatedLastPosition,
    handleAddToSubPlan,
    handleRemoveFromSubPlan,
    selectedPlanItems,
    selectPlanItem,
    removePlanItem: handleRemoveSelectedPlanItem
  }

  return (
    <MinimizedObjectivesListActionsContext.Provider value={value}>
      {children}
    </MinimizedObjectivesListActionsContext.Provider>
  )
}

export default MinimizedObjectivesListActions
